/**
 * YouTubeEmbed — lecteur YouTube intégré responsive (16/9).
 *
 * Accepte n'importe quelle URL YouTube standard :
 *   - https://www.youtube.com/watch?v=VIDEO_ID
 *   - https://youtu.be/VIDEO_ID
 *   - https://www.youtube.com/embed/VIDEO_ID
 *   - https://www.youtube.com/shorts/VIDEO_ID
 *
 * Rend `null` si l'URL n'est pas reconnue (silencieux, jamais d'erreur).
 */
import { Youtube } from "lucide-react";

export function extractYouTubeId(url) {
  if (!url || typeof url !== "string") return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  // Pattern global couvrant watch?v=, youtu.be/, embed/, shorts/
  const patterns = [
    /(?:youtube\.com\/watch\?[^#]*v=)([A-Za-z0-9_-]{6,20})/,
    /(?:youtu\.be\/)([A-Za-z0-9_-]{6,20})/,
    /(?:youtube\.com\/embed\/)([A-Za-z0-9_-]{6,20})/,
    /(?:youtube\.com\/shorts\/)([A-Za-z0-9_-]{6,20})/,
    /(?:youtube-nocookie\.com\/embed\/)([A-Za-z0-9_-]{6,20})/,
  ];
  for (const re of patterns) {
    const m = trimmed.match(re);
    if (m?.[1]) return m[1];
  }
  return null;
}

export function isYouTubeUrl(url) {
  return !!extractYouTubeId(url);
}

export default function YouTubeEmbed({ url, title = "Vidéo YouTube", className = "" }) {
  const videoId = extractYouTubeId(url);
  if (!videoId) {
    // Fallback : lien externe si pas parsable
    return (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className={`inline-flex items-center gap-1.5 text-sm text-[hsl(var(--aito-lagoon))] hover:underline ${className}`}
      >
        <Youtube className="h-4 w-4" />
        Ouvrir la vidéo
      </a>
    );
  }
  const embedUrl = `https://www.youtube-nocookie.com/embed/${videoId}?rel=0`;
  return (
    <div
      className={`relative w-full overflow-hidden rounded-lg bg-black ${className}`}
      style={{ aspectRatio: "16 / 9" }}
      data-testid="youtube-embed"
    >
      <iframe
        src={embedUrl}
        title={title}
        loading="lazy"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        referrerPolicy="strict-origin-when-cross-origin"
        className="absolute inset-0 h-full w-full border-0"
      />
    </div>
  );
}
